'use client'

import { effectiveSellerPrice } from '@/components/client/product/prices/price-section-types'
import { isMonthlyPriceType } from '@/components/client/product/prices/price-step'
import { useProductPurchase } from '@/components/client/product/prices/purchase-context'
import { Anchor } from '@/components/client/seo/anchor'
import { useTranslation } from '@/hooks/client/use-translation'
import { formatOfferPrice as formatPrice } from '@/lib/prices/format-offer-price'

import { preferredScorecardOffer } from './preferred-scorecard-offer'

import { ScorecardLearnMore } from '../scorecard-learn-more'

export const ScorecardBuyingSummary = () => {
  const purchase = useProductPurchase()
  const { t } = useTranslation()
  if (!purchase) {
    return null
  }
  const { sellers, data } = purchase
  // Use the same preferred-seller rule as the product's primary deal, within
  // the selected model and tier. The comparison below still sorts by price.
  const offer = preferredScorecardOffer(sellers)

  return (
    <div data-scorecard-buying className="mx-6 shrink-0 border-t border-white/20 pt-5 pb-3">
      {offer?.url ? (
        <>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-semibold">{offer.sellerName}</p>
              {offer.care?.type && <p className="mt-1 text-sm text-white/65">{offer.care.type}</p>}
            </div>
            <div className="shrink-0 text-right">
              {offer.price > 0 ? (
                <>
                  <p className="text-2xl leading-tight font-semibold tabular-nums">
                    {formatPrice(effectiveSellerPrice(offer))}
                  </p>
                  <p className="mt-1 text-sm text-white/65">
                    {isMonthlyPriceType(offer.priceType)
                      ? t('price.perPairMonthlyPhrase', 'per pair, per month')
                      : t('price.perPairPhrase', 'per pair')}
                  </p>
                </>
              ) : (
                <p className="text-sm text-white/65">{t('product.purchase.noPrice', 'No price available')}</p>
              )}
            </div>
          </div>
          <ScorecardLearnMore
            url={offer.url}
            label={t('product.purchase.viewDealAt', 'View deal at {{seller}}', { seller: offer.sellerName })}
            tracking={{
              seller: offer.sellerName,
              sellerType: offer.sellerType,
              release: data.releaseSlug,
              position: 'product-scorecard',
            }}
            className="flex w-full rounded-lg bg-blue-600 py-3 shadow-none transition-colors hover:bg-blue-700 hover:shadow-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky active:scale-100"
          />
        </>
      ) : (
        <p className="mb-4 text-sm leading-relaxed text-white/70">
          {t('product.purchase.noOffers', 'No offers are available for this configuration.')}
        </p>
      )}
      <Anchor
        href="#prices"
        data-tracking="ignore"
        className="mt-2 block cursor-pointer py-1 text-center text-xs text-white/70 underline underline-offset-4 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-sky"
      >
        {t('product.purchase.title', 'Compare prices & care')}
      </Anchor>
    </div>
  )
}
